import { type ChannelAlias, toBackendChannel } from "./channel-map";

function qs(params: Record<string, string | number | undefined>): string {
  // Strict params: drop undefined AND empty strings, so identical user intents
  // hash to the same URL (max backend cache hit rate).
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "") continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join("&")}` : "";
}

export interface ProductSearchInput {
  channel: ChannelAlias;
  keyword: string;
  pageNo?: number;
  pageSize?: number;
  sort?: string;
}

export function buildProductSearchUrl(input: ProductSearchInput): string {
  return `/product/search/keyword${qs({
    channel: toBackendChannel(input.channel),
    keyword: input.keyword,
    pageNo: input.pageNo,
    pageSize: input.pageSize,
    sort: input.sort,
  })}`;
}

export interface ProductDetailInput {
  channel: ChannelAlias;
  spuNo: string;
  refresh?: 0 | 1;
  activityCode?: string;
}

export function buildProductDetailUrl(input: ProductDetailInput): string {
  return `/product/detail${qs({
    channel: toBackendChannel(input.channel),
    spuNo: input.spuNo,
    refresh: input.refresh,
    activityCode: input.activityCode,
  })}`;
}

/** Backend ProductSearchParam.sort accepts: default / sales_asc / sales_desc
 * / price_asc / price_desc (1688 also: repurchase_asc / repurchase_desc; weidian ignored).
 * Pass `default` or omit to disable sorting. */
export type BackendSort =
  | "default"
  | "sales_asc"
  | "sales_desc"
  | "price_asc"
  | "price_desc"
  | "repurchase_asc"
  | "repurchase_desc";

export interface ProductHotSaleInput {
  channel: ChannelAlias;
  categoryId?: string;
  keyword?: string;
  pageNo: number;
  pageSize: number;
  sort?: BackendSort;
  /** ₽ price floor (inclusive), passed to backend as `priceStart` */
  priceStart?: string;
  /** ₽ price ceiling (inclusive), passed to backend as `priceEnd` */
  priceEnd?: string;
}

export function buildProductHotSaleUrl(input: ProductHotSaleInput): string {
  return `/product/hot/sale/list${qs({
    channel: toBackendChannel(input.channel),
    // Backend (#220) filters the mixed-platform endpoint by `frontCategoryCode`
    // (the real column), not `categoryId`. Keep the FE-internal name `categoryId`
    // but send it under the param key the backend actually reads.
    frontCategoryCode: input.categoryId,
    keyword: input.keyword,
    pageNo: input.pageNo,
    pageSize: input.pageSize,
    sort: input.sort,
    priceStart: input.priceStart,
    priceEnd: input.priceEnd,
  })}`;
}

export interface ProductRecommendInput {
  channel: ChannelAlias;
  spuNo: string;
  pageNo: number;
  pageSize: number;
}

export function buildProductRecommendUrl(input: ProductRecommendInput): string {
  return `/product/recommend/list${qs({
    channel: toBackendChannel(input.channel),
    spuNo: input.spuNo,
    pageNo: input.pageNo,
    pageSize: input.pageSize,
  })}`;
}

/** PDP-scoped recommend: `/product/recommend?spuNo&channel` — returns a flat list. */
export interface ProductPdpRecommendInput {
  channel: ChannelAlias;
  spuNo: string;
}

export function buildProductPdpRecommendUrl(input: ProductPdpRecommendInput): string {
  return `/product/recommend${qs({
    channel: toBackendChannel(input.channel),
    spuNo: input.spuNo,
  })}`;
}

export interface ProductQcPhotosInput {
  channel: ChannelAlias;
  spuNo: string;
  skuNo: string;
}

/** SKU-scoped warehouse QC photos for PDP. */
export function buildProductQcPhotosUrl(input: ProductQcPhotosInput): string {
  return `/product/qc-photos${qs({
    channel: toBackendChannel(input.channel),
    spuNo: input.spuNo,
    skuNo: input.skuNo,
  })}`;
}

/** Shop detail: `/shop/pub/info?shopId&channel`. `shopId` = channelShopCode. */
export interface ShopDetailInput {
  channel: ChannelAlias;
  /** channelShopCode — the per-channel shop identifier (route param). */
  shopCode: string;
}

export function buildShopDetailUrl(input: ShopDetailInput): string {
  return `/shop/pub/info${qs({
    channel: toBackendChannel(input.channel),
    shopId: input.shopCode,
  })}`;
}

/**
 * Shop goods list: `/shop/pub/goods/list?shopId&channel&page&pageSize&sort`.
 * Backend caps `page` at 10 (MAX_SHOP_GOODS_PAGE); past that it returns empty +
 * hasMore=0. We omit sort/price filters here to keep the shop page to a single
 * unfiltered list (matches the converged #186 scope).
 */
export interface ShopGoodsInput {
  channel: ChannelAlias;
  /** channelShopCode. */
  shopCode: string;
  page: number;
  pageSize: number;
  sort?: BackendSort;
  keyword?: string;
}

export function buildShopGoodsUrl(input: ShopGoodsInput): string {
  return `/shop/pub/goods/list${qs({
    channel: toBackendChannel(input.channel),
    shopId: input.shopCode,
    page: input.page,
    pageSize: input.pageSize,
    sort: input.sort && input.sort !== "default" ? input.sort : undefined,
    keyword: input.keyword || undefined,
  })}`;
}

/** Cart list V2: `/shopcart/listV2?keyword&source&lang` — uses JWT for user scope. */
export interface CartListInput {
  keyword?: string;
  /** 1 = self-platform default. See AddShopCartSourceEnum on backend. */
  source?: number;
  /** Backend lang for subject translation (zh_CN / ru_RU / en_US). */
  lang?: string;
}

export function buildCartListUrl(input: CartListInput = {}): string {
  return `/shopcart/listV2${qs({
    keyword: input.keyword,
    source: input.source,
    lang: input.lang,
  })}`;
}

export interface ProductSearchPagedInput {
  channel: ChannelAlias;
  keyword: string;
  categoryId?: string;
  pageNo: number;
  pageSize: number;
  sort?: BackendSort;
  /** ₽ price floor (inclusive), passed to backend as `priceStart` */
  priceStart?: string;
  /** ₽ price ceiling (inclusive), passed to backend as `priceEnd` */
  priceEnd?: string;
}

export function buildProductSearchPagedUrl(input: ProductSearchPagedInput): string {
  return `/product/search/keyword${qs({
    channel: toBackendChannel(input.channel),
    keyword: input.keyword,
    categoryId: input.categoryId,
    pageNo: input.pageNo,
    pageSize: input.pageSize,
    sort: input.sort,
    priceStart: input.priceStart,
    priceEnd: input.priceEnd,
  })}`;
}

// Apple was removed platform-wide (#87) — the backend render endpoint no longer
// supports it, so it's dropped from the FE-initiated source union to prevent
// misuse. Historical Apple *bindings* may still appear in `ThirdLoginBinding`.
export type ThirdSource = "google" | "telegram" | "yandex" | "vk";

export interface ThirdLoginRenderInput {
  source: ThirdSource;
  countryCode?: string;
  currencyCode?: string;
  lang?: string;
  inviteCode?: string;
  activityCode?: string;
  utmParam?: string;
  callback?: string;
}

export function buildThirdLoginRenderUrl(input: ThirdLoginRenderInput): string {
  const { source, ...rest } = input;
  return `/sys/thirdLogin/render/${source}${qs(rest)}`;
}

export interface ThirdLoginCallbackInput {
  source: ThirdSource;
  code: string;
  state?: string;
}

/**
 * Login-completion endpoint for the render→redirect OAuth flow (#649).
 *
 * After the provider redirects the browser back to the registered callback
 * (`https://<host>/auth/callback?code=&state=`), the landing page hands the
 * raw `code`/`state` to the backend to exchange for a session JWT. This is the
 * step aps-web did (`getCallback`) but the kupikupi callback page originally
 * skipped — it only read `?token=`, which nothing ever sends.
 *
 * google / yandex → `GET /sys/thirdLogin/{source}/callback?code=&state=`
 *   (aps-boot `ThirdLoginController.loginThird`: reads the render context keyed
 *   by `state`, so only code+state travel here — lang/inviteCode/redirect_uri
 *   were stored at render time). telegram → see {@link buildTelegramOauth2Url}.
 */
export function buildThirdLoginCallbackUrl(input: ThirdLoginCallbackInput): string {
  const { source, ...rest } = input;
  return `/sys/thirdLogin/${source}/callback${qs(rest)}`;
}

/**
 * Telegram OIDC login-completion (aps-boot `/telegram/oauth2`, POST {code,state}).
 * Distinct from google/yandex: Telegram completion is a POST that re-derives
 * the OIDC authorization session by `state` rather than a JustAuth GET callback.
 */
export function buildTelegramOauth2Url(): string {
  return "/sys/thirdLogin/telegram/oauth2";
}

/**
 * Binds a third-party provider to the *current* (logged-in) user.
 *
 * `POST /sys/thirdLogin/bind/{source}` (aps-boot #216) on a signature-verified
 * route (#220). Unlike the login *render* flow, this consumes a client-obtained
 * provider credential directly:
 *   - google / yandex → `{ code }`   (OAuth authorization code)
 *   - telegram        → `{ initData }` (Telegram Mini App initData string)
 * Requires the caller's signed JWT in the `X-Access-Token` header.
 */
export function buildThirdLoginBindUrl(source: ThirdSource): string {
  return `/sys/thirdLogin/bind/${source}`;
}

/**
 * Telegram Mini App silent login (aps-boot `ThirdLoginController.telegramMiniAppInitDataLogin`).
 *
 * `POST /sys/thirdLogin/telegram/initData` — distinct from the OAuth `render`
 * flow used by the public web. Inside a Telegram Mini App the WebView already
 * carries a signed `initData` string, so there is no browser redirect: the
 * client posts the raw `initData` and the backend HMAC-verifies it against the
 * bot token (stateless) and returns a `LoginVo` with a fresh JWT
 * (`result.xaccessToken`). No password, no captcha, no session round-trip.
 *
 * The request body may include `inviteCode`; aps-boot binds it during Mini App
 * auto-registration so the invited user can generate future shipping commission.
 */
export function buildTelegramInitDataLoginUrl(): string {
  return "/sys/thirdLogin/telegram/initData";
}

export interface OrderListUrlInput {
  status?: string;
  subject?: string;
  orderNo?: string;
  pageNo?: number;
  pageSize?: number;
  hideCancelOrder?: 0 | 1;
  /** 1 = only items awaiting photo confirmation (backend `OrderQueryParam.isQueryWaitConfirm`). */
  isQueryWaitConfirm?: 0 | 1;
  /** Inclusive lower bound on order create time. Backend `OrderQueryParam.createTimeStart`
   *  (#216) — expects `yyyy-MM-dd HH:mm:ss` (or `yyyy-MM-dd`). */
  createTimeStart?: string;
  /** Inclusive upper bound on order create time. Backend `OrderQueryParam.createTimeEnd` (#216). */
  createTimeEnd?: string;
  /** Source channel/platform alias (taobao / 1688 / weidian / merch). Mapped to the
   *  backend's uppercase channel value via `toBackendChannel`. Backend `OrderQueryParam.channel` (#216). */
  channel?: ChannelAlias;
}

export function buildOrderListUrl(input: OrderListUrlInput = {}): string {
  return `/order/listV2${qs({
    status: input.status,
    subject: input.subject,
    orderNo: input.orderNo,
    pageNo: input.pageNo,
    pageSize: input.pageSize,
    hideCancelOrder: input.hideCancelOrder,
    isQueryWaitConfirm: input.isQueryWaitConfirm,
    createTimeStart: input.createTimeStart,
    createTimeEnd: input.createTimeEnd,
    channel: input.channel ? toBackendChannel(input.channel) : undefined,
  })}`;
}

export interface StoredOrderListUrlInput {
  isParcelable?: "Y" | "N";
  orderNo?: string;
  createTimeStart?: string;
  createTimeEnd?: string;
  pageNo?: number;
  pageSize?: number;
}

export function buildStoredOrderListUrl(input: StoredOrderListUrlInput = {}): string {
  return `/order/stored/list@2${qs({
    isParcelable: input.isParcelable ?? "Y",
    orderNo: input.orderNo,
    createTimeStart: input.createTimeStart,
    createTimeEnd: input.createTimeEnd,
    pageNo: input.pageNo,
    pageSize: input.pageSize,
  })}`;
}

export interface FavoritePageUrlInput {
  type?: "spu" | "shop";
  channel?: ChannelAlias;
  pageNo?: number;
  pageSize?: number;
}

export function buildFavoritePageUrl(input: FavoritePageUrlInput = {}): string {
  return `/favorites/page${qs({
    type: input.type ?? "spu",
    channel: input.channel ? toBackendChannel(input.channel) : undefined,
    pageNo: input.pageNo,
    pageSize: input.pageSize,
  })}`;
}

export function buildAddressListUrl(): string {
  return "/address/customer/list";
}

export interface ProductImageSearchInput {
  imageId: string;
  channel: ChannelAlias;
  pageNo: number;
  pageSize: number;
}

/**
 * Image-based product search. Distinct from keyword search:
 * backend explicitly disables sort/filter for this endpoint, so we don't
 * accept those params here — see `_e-catalog.css` / `CatalogPageClient`
 * for the corresponding UI gating.
 */
export function buildProductImageSearchUrl(input: ProductImageSearchInput): string {
  return `/product/search/image${qs({
    imageId: input.imageId,
    channel: toBackendChannel(input.channel),
    pageNo: input.pageNo,
    pageSize: input.pageSize,
  })}`;
}

export interface OrderDetailUrlInput {
  orderNo: string;
}

export function buildOrderDetailUrl(input: OrderDetailUrlInput): string {
  return `/order/detailV2${qs({ orderNo: input.orderNo })}`;
}
